import { startTransition } from "react";

import { setStageCovered } from "@/components/motion/stage";

import { PAGE_TRANSITION } from "./variant";

// =============================================================================
// Page transitions of the public website (variant chosen in variant.ts):
// - "clip", the one in use: a native View Transition, the old page recedes as
//   a card while the new one opens from the bottom;
// - "curtain", kept aside: ink strips cover the screen with the name of the
//   next page, the route changes underneath, then the strips lift.
// Either way the entrance animations of the new page wait for the stage to be
// uncovered (see stage.ts), so they play when the page shows up.
// =============================================================================

export type PageTarget = {
  /** Localized href of the destination ("/en/projects") */
  href: string;
  /** Locale of the destination */
  locale: string;
};

/** Overlay drawing the curtain (PageCurtain) */
export type CurtainHandle = {
  /** Covers the screen with the name of the destination */
  cover(target: PageTarget): Promise<void>;
  /** Covers the screen at once (browser back and forward) */
  coverNow(): void;
  /** Lifts the curtain off the new page, faster after a history move */
  reveal(quick: boolean): Promise<void>;
};

/** Past this delay the curtain lifts even if the route did not change */
const COMMIT_TIMEOUT_MS = 6000;
const HISTORY_COMMIT_TIMEOUT_MS = 1500;
/** Shortest time the screen stays covered, for the page name to be read */
const MIN_COVER_MS = 220;

let curtain: CurtainHandle | null = null;
let isRunning = false;
let committedPathname: string | null = null;
const commitListeners = new Set<() => void>();

// =============================================================================

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Resolves on the next commit of a new pathname (false on timeout) */
function waitForCommit(timeout: number): Promise<boolean> {
  return new Promise((resolve) => {
    const finish = (committed: boolean) => {
      clearTimeout(timer);
      commitListeners.delete(onCommit);
      resolve(committed);
    };
    const onCommit = () => finish(true);
    const timer = setTimeout(() => finish(false), timeout);
    commitListeners.add(onCommit);
  });
}

// =============================================================================

export function registerCurtain(handle: CurtainHandle): void {
  curtain = handle;
}

export function unregisterCurtain(handle: CurtainHandle): void {
  if (curtain === handle) curtain = null;
}

/** Called after each commit with the pathname of the rendered route */
export function notifyRouteCommit(pathname: string): void {
  if (pathname === committedPathname) return;
  committedPathname = pathname;
  for (const listener of [...commitListeners]) listener();
}

// =============================================================================

/**
 * Runs navigate (router push or replace) inside the page transition. Same
 * page navigations (filters, anchors) run as is.
 */
export function navigateWithTransition(
  target: PageTarget,
  navigate: () => void
): void {
  const url = new URL(target.href, window.location.href);
  if (isRunning || url.pathname === window.location.pathname) {
    navigate();
    return;
  }

  isRunning = true;
  const transition =
    PAGE_TRANSITION === "clip"
      ? clipTransition(navigate)
      : curtainTransition(target, navigate);
  transition.finally(() => {
    isRunning = false;
  });
}

async function curtainTransition(target: PageTarget, navigate: () => void) {
  const handle = curtain;
  if (!handle) {
    navigate();
    return;
  }

  await handle.cover(target);
  // The new page mounts under the curtain: its entrance waits for the reveal
  setStageCovered(true);
  const coveredAt = performance.now();
  const committed = waitForCommit(COMMIT_TIMEOUT_MS);
  startTransition(navigate);
  await committed;
  // Let the new page paint (and scroll to the top) before lifting
  await nextFrame();
  await nextFrame();
  const minCover = prefersReducedMotion() ? 0 : MIN_COVER_MS;
  const elapsed = performance.now() - coveredAt;
  if (elapsed < minCover) await wait(minCover - elapsed);

  setStageCovered(false);
  // A language switch remounts the curtain: lift the one on screen now
  await (curtain ?? handle).reveal(false);
}

async function clipTransition(navigate: () => void) {
  if (!("startViewTransition" in document) || prefersReducedMotion()) {
    navigate();
    return;
  }

  setStageCovered(true);
  const committed = waitForCommit(COMMIT_TIMEOUT_MS);
  const transition = document.startViewTransition(async () => {
    startTransition(navigate);
    await committed;
  });
  // The new page is captured with its entrance pending, which then plays
  // live inside the opening clip
  await transition.ready.catch(() => {});
  setStageCovered(false);
  await transition.finished.catch(() => {});
}

/**
 * Browser back and forward: the URL has already changed, so the curtain
 * covers the screen at once and lifts quickly once the page is rendered.
 */
export function onHistoryNavigation(): void {
  const handle = curtain;
  if (
    PAGE_TRANSITION !== "curtain" ||
    !handle ||
    isRunning ||
    document.hidden ||
    window.location.pathname === committedPathname
  ) {
    return;
  }

  isRunning = true;
  handle.coverNow();
  setStageCovered(true);
  waitForCommit(HISTORY_COMMIT_TIMEOUT_MS)
    .then(nextFrame)
    .then(() => {
      setStageCovered(false);
      return (curtain ?? handle).reveal(true);
    })
    .finally(() => {
      isRunning = false;
    });
}

// =============================================================================
